import LandingNavbar from '../../components/landing/landing-navbar';
import LandingFooter from '../../components/landing/landing-footer';
import { Clock, Mail, MapPin, Phone, Gift, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiService } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { getTenantId } from '../../lib/config';
import { useAuth } from '../../contexts/auth-context';
import { useNavigate } from 'react-router-dom';
import LandingImageCarousel from '@/components/landing/landing-image-carousel';

interface Company {
  id: string;
  name: string;
}

interface Room {
  id: string;
  roomNumber: string;
  roomTypeId: string;
  companyId: string;
  basePricePerNight: number;
  isActive: boolean;
  capacity?: number;
  roomTypeRef?: {
    id: string;
    name: string;
  };
  reservations?: Array<{
    id: string;
    checkInDate: string;
    checkOutDate: string;
    status: string;
    reservationNumber?: string;
  }>;
}

interface RoomType {
  id: string;
  name: string;
  companyId?: string; // Optional, for filtering by company
}

export default function ContactPage() {
  const { toast } = useToast();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [loading, setLoading] = useState(false);
  const [roomTypesLoaded, setRoomTypesLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availableRooms, setAvailableRooms] = useState<any[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string>('');
  const [promotions, setPromotions] = useState<any[]>([]);
  const [appliedPromotions, setAppliedPromotions] = useState<any[]>([]);
  const [hasCheckedAvailability, setHasCheckedAvailability] = useState(false);
  const [isContactVisible, setIsContactVisible] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const contactSectionRef = useRef<HTMLDivElement>(null);

  const contactInfo = [
    {
      icon: MapPin,
      label: 'ADDRESS:',
      value: 'KK 587A St, Rebero, Kigali, Rwanda',
    },
    {
      icon: Phone,
      label: 'HOTEL TELEPHONE:',
      value: '+250 791 874 212',
    },
    {
      icon: Mail,
      label: 'EMAIL:',
      value: 'info@ikorohouse.com',
    },
    {
      icon: MapPin,
      label: 'GENERAL MANAGER',
      value: 'Gaston Rurangirwa',
    },
    {
      icon: Clock,
      label: 'CHECK-IN / CHECK-OUT:',
      value: '14h00 / 11h00',
    },
  ];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    hotel: '',
    roomType: '',
    numberOfGuests: '',
    arrivingDate: '',
    departureDate: '',
    promotionId: '',
  });

  // Load accommodations on mount
  useEffect(() => {
    const loadAccommodations = async () => {
      try {
        setLoading(true);
        const tenantId = getTenantId();
        const response = await fetch(`${apiService['baseUrl']}/api/public/accommodations?tenantId=${tenantId}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch accommodations: ${response.statusText}`);
        }

        const data = await response.json();
        
        if (data.success && data.data) {
          const activeCompanies = data.data.companies || [];
          const allRooms = data.data.rooms || [];
          const allRoomTypes = data.data.roomTypes || [];

          setCompanies(activeCompanies);
          setRooms(allRooms);

          // Use room types from API if available, otherwise extract from rooms
          if (allRoomTypes.length > 0) {
            const mappedTypes = allRoomTypes.map((rt: any) => ({
              id: rt.id,
              name: rt.name,
              companyId: rt.companyId, // Store companyId for filtering
            }));
            setRoomTypes(mappedTypes);
            setRoomTypesLoaded(true);
          } else {
            // Fallback: Extract unique room types from rooms
            const roomTypesMap = new Map<string, RoomType>();
            allRooms.forEach((room: Room) => {
              if (room.roomTypeRef) {
                roomTypesMap.set(room.roomTypeRef.id, {
                  id: room.roomTypeRef.id,
                  name: room.roomTypeRef.name,
                  companyId: room.companyId, // Store companyId from room
                });
              }
            });
            setRoomTypes(Array.from(roomTypesMap.values()));
            setRoomTypesLoaded(true);
          }
        }
      } catch (err: any) {
        console.error("Failed to load accommodations:", err);
        toast({
          title: 'Error',
          description: 'Failed to load hotel information. Please refresh the page.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadAccommodations();
    
      // Restore form data if user just logged in
      if (isAuthenticated && user) {
        const savedFormData = localStorage.getItem('reservationFormData');
        if (savedFormData) {
          try {
            const parsed = JSON.parse(savedFormData);
            setFormData(parsed);
            // Load room types and promotions if hotel is restored
            if (parsed.hotel) {
              setTimeout(async () => {
                try {
                  const types = await apiService.getHotelRoomTypes(parsed.hotel);
                  setRoomTypes(Array.isArray(types) ? types.map((t: any) => ({
                    id: t.id,
                    name: t.name,
                    companyId: t.companyId,
                  })) : []);
                  setRoomTypesLoaded(true);
                } catch (error) {
                  // Fallback to filtering from loaded room types
                  setRoomTypes((prevTypes) => {
                    const filtered = prevTypes.filter((rt: RoomType) => rt.companyId === parsed.hotel);
                    setRoomTypesLoaded(true);
                    return filtered;
                  });
                }
                loadPromotions(parsed.hotel);
              }, 100);
            }
            localStorage.removeItem('reservationFormData');
            localStorage.removeItem('reservationReturnUrl');
          } catch (error) {
            console.error('Failed to restore form data:', error);
          }
        }
      }
    }, [toast, isAuthenticated, user]);

  // Clear selection if selected room becomes conflicting when dates change
  useEffect(() => {
    if (selectedRoomId && formData.arrivingDate && formData.departureDate) {
      const selectedRoomDetails = rooms.find((r) => r.id === selectedRoomId);
      if (selectedRoomDetails) {
        const hasConflict = isRoomReservedForDates(selectedRoomDetails, formData.arrivingDate, formData.departureDate);
        if (hasConflict) {
          setSelectedRoomId('');
          setAppliedPromotions([]);
          setFormData(prev => ({ ...prev, promotionId: '' }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.arrivingDate, formData.departureDate, selectedRoomId, rooms]);

  // Intersection Observer for contact section animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update visibility based on whether element is in viewport
        setIsContactVisible(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    if (contactSectionRef.current) {
      observer.observe(contactSectionRef.current);
    }

    return () => {
      if (contactSectionRef.current) {
        observer.unobserve(contactSectionRef.current);
      }
    };
  }, []);

  // Load promotions for a company
  const loadPromotions = async (companyId: string) => {
    if (!companyId) {
      setPromotions([]);
      return;
    }
    try {
      const promos = await apiService.getPromotions({
        companyId,
        isActive: true,
      });
      setPromotions(Array.isArray(promos) ? promos : []);
    } catch (error: any) {
      console.error('Failed to load promotions:', error);
      setPromotions([]);
    }
  };

  // Auto-apply valid promotions
  const autoApplyPromotion = () => {
    if (!formData.hotel || !formData.roomType || !formData.arrivingDate || !formData.departureDate || !selectedRoomId) {
      setAppliedPromotions([]);
      setFormData(prev => ({ ...prev, promotionId: '' }));
      return;
    }

    const selectedRoom = availableRooms.find((r: any) => r.id === selectedRoomId);
    if (!selectedRoom) {
      setAppliedPromotions([]);
      setFormData(prev => ({ ...prev, promotionId: '' }));
      return;
    }

    const promosToCheck = promotions.filter((p: any) => 
      p.companyId === formData.hotel
    );

    if (promosToCheck.length === 0) {
      setAppliedPromotions([]);
      setFormData(prev => ({ ...prev, promotionId: '' }));
      return;
    }

    const now = new Date();
    const checkIn = new Date(formData.arrivingDate);
    const checkOut = new Date(formData.departureDate);
    const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));

    // Filter valid promotions
    const validPromotions = promosToCheck.filter((promo: any) => {
      const validFrom = new Date(promo.validFrom);
      const validTo = new Date(promo.validTo);
      
      if (!promo.isActive) return false;
      if (validFrom > now || validTo < now) return false;
      if (checkIn < validFrom || checkIn > validTo) return false;
      
      if (promo.minStay && nights < promo.minStay) return false;
      if (promo.maxStay && nights > promo.maxStay) return false;
      
      if (promo.maxUses && promo.currentUses >= promo.maxUses) return false;
      
      if (promo.applicableRoomTypes && Array.isArray(promo.applicableRoomTypes) && promo.applicableRoomTypes.length > 0) {
        if (!promo.applicableRoomTypes.includes(formData.roomType)) return false;
      }
      
      return true;
    });

    if (validPromotions.length === 0) {
      setAppliedPromotions([]);
      setFormData(prev => ({ ...prev, promotionId: '' }));
      return;
    }

    const baseAmount = (selectedRoom.basePricePerNight || 0) * nights;
    let totalDiscount = 0;
    let remainingAmount = baseAmount;

    const promotionsWithDiscounts = validPromotions.map((promo: any) => {
      let discount = 0;
      if (promo.discountType === 'PERCENTAGE') {
        discount = remainingAmount * (parseFloat(promo.discountValue.toString()) / 100);
        remainingAmount = remainingAmount - discount;
      } else if (promo.discountType === 'FIXED_AMOUNT') {
        discount = parseFloat(promo.discountValue.toString());
        remainingAmount = Math.max(0, remainingAmount - discount);
      } else if (promo.discountType === 'FREE_NIGHT') {
        const freeNights = parseInt(promo.discountValue.toString());
        discount = (selectedRoom.basePricePerNight || 0) * freeNights;
        remainingAmount = Math.max(0, remainingAmount - discount);
      }
      
      totalDiscount += discount;
      return { ...promo, calculatedDiscount: discount };
    });

    if (promotionsWithDiscounts.length > 0) {
      setAppliedPromotions(promotionsWithDiscounts);
      setFormData(prev => ({ ...prev, promotionId: promotionsWithDiscounts[0].id }));
    } else {
      setAppliedPromotions([]);
      setFormData(prev => ({ ...prev, promotionId: '' }));
    }
  };

  // Load room types for a company
  const loadRoomTypesForCompany = async (companyId: string) => {
    if (!companyId) {
      setRoomTypes([]);
      setRoomTypesLoaded(true);
      return;
    }
    setRoomTypesLoaded(false);
    
    // Try multiple approaches to ensure room types are loaded
    try {
      // Approach 1: Try using apiService
      const types = await apiService.getHotelRoomTypes(companyId);
      
      if (Array.isArray(types) && types.length > 0) {
        const mappedTypes = types.map((t: any) => ({
          id: t.id,
          name: t.name,
          companyId: t.companyId || companyId,
        }));
        setRoomTypes(mappedTypes);
        setRoomTypesLoaded(true);
        return;
      }
    } catch (error: any) {
      // Silently continue to fallback
    }
    
    // Approach 2: Try direct fetch as fallback
    try {
      const url = `${apiService['baseUrl']}/api/hotel/room-types?companyId=${companyId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const types = await response.json();
        
        if (Array.isArray(types) && types.length > 0) {
          const mappedTypes = types.map((t: any) => ({
            id: t.id,
            name: t.name,
            companyId: t.companyId || companyId,
          }));
          setRoomTypes(mappedTypes);
          setRoomTypesLoaded(true);
          return;
        }
      }
    } catch (error: any) {
      // Silently continue to fallback
    }
    
    // Approach 3: Fallback to already loaded room types
    const fallbackTypes = roomTypes.filter((rt: RoomType) => 
      rt.companyId === companyId
    );
    
    if (fallbackTypes.length > 0) {
      setRoomTypes(fallbackTypes);
      setRoomTypesLoaded(true);
      return;
    }
    
    // Approach 4: Last resort - Extract from rooms
    const roomTypesMap = new Map<string, RoomType>();
    rooms.forEach((room: Room) => {
      if (room.companyId === companyId && room.roomTypeRef) {
        roomTypesMap.set(room.roomTypeRef.id, {
          id: room.roomTypeRef.id,
          name: room.roomTypeRef.name,
          companyId: room.companyId,
        });
      }
    });
    setRoomTypes(Array.from(roomTypesMap.values()));
    setRoomTypesLoaded(true);
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Reset room type when hotel changes
    if (field === 'hotel') {
      setFormData(prev => ({ ...prev, roomType: '', arrivingDate: '', departureDate: '', promotionId: '' }));
      setAvailableRooms([]);
      setSelectedRoomId('');
      setAppliedPromotions([]);
      setHasCheckedAvailability(false);
      // Filter room types by selected hotel from already loaded room types
      if (value) {
        setRoomTypesLoaded(false);
        const tenantId = getTenantId();
        fetch(`${apiService['baseUrl']}/api/public/accommodations?tenantId=${tenantId}`)
          .then(res => res.json())
          .then(data => {
            if (data.success && data.data && data.data.roomTypes) {
              const allTypes = data.data.roomTypes.map((rt: any) => ({
                id: rt.id,
                name: rt.name,
                companyId: rt.companyId,
              }));
              const filteredTypes = allTypes.filter((rt: RoomType) => rt.companyId === value);
              setRoomTypes(filteredTypes);
              setRoomTypesLoaded(true);
            } else {
              setRoomTypesLoaded(true);
            }
          })
          .catch(() => {
            setRoomTypes((prevTypes) => {
              const filtered = prevTypes.filter((rt: RoomType) => rt.companyId === value);
              setRoomTypesLoaded(true);
              return filtered;
            });
          });
        loadRoomTypesForCompany(value);
        loadPromotions(value);
      } else {
        setRoomTypesLoaded(false);
        const tenantId = getTenantId();
        fetch(`${apiService['baseUrl']}/api/public/accommodations?tenantId=${tenantId}`)
          .then(res => res.json())
          .then(data => {
            if (data.success && data.data && data.data.roomTypes) {
              setRoomTypes(data.data.roomTypes.map((rt: any) => ({
                id: rt.id,
                name: rt.name,
                companyId: rt.companyId,
              })));
            }
            setRoomTypesLoaded(true);
          })
          .catch(() => {
            setRoomTypesLoaded(true);
          });
        setPromotions([]);
      }
    }
    
    // Reset room selection when room type, dates, or guests change
    if (field === 'roomType' || field === 'arrivingDate' || field === 'departureDate' || field === 'numberOfGuests') {
      setAvailableRooms([]);
      setSelectedRoomId('');
      setAppliedPromotions([]);
      setHasCheckedAvailability(false);
      setFormData(prev => ({ ...prev, promotionId: '' }));
    }
  };

  // Check if a room has any active reservations (like accommodations page)
  const isRoomReserved = (room: Room): boolean => {
    if (!room.reservations || room.reservations.length === 0) return false;
    return room.reservations.some((r: any) => 
      r.status !== 'CANCELLED' && r.status !== 'CHECKED_OUT'
    );
  };

  // Check if a room is reserved for the selected dates (conflict check)
  const isRoomReservedForDates = (room: Room, checkInDate: string, checkOutDate: string): boolean => {
    if (!room.reservations || room.reservations.length === 0) return false;
    
    // Parse and normalize dates to midnight UTC for consistent comparison
    const checkIn = new Date(checkInDate);
    checkIn.setUTCHours(0, 0, 0, 0);
    const checkOut = new Date(checkOutDate);
    checkOut.setUTCHours(0, 0, 0, 0);
    
    return room.reservations.some((res: any) => {
      if (res.status === 'CANCELLED' || res.status === 'CHECKED_OUT') return false;
      
      const resCheckIn = new Date(res.checkInDate);
      resCheckIn.setUTCHours(0, 0, 0, 0);
      const resCheckOut = new Date(res.checkOutDate);
      resCheckOut.setUTCHours(0, 0, 0, 0);
      
      // Hotel booking convention: checkout day is when room becomes available
      // Occupied nights are from check-in to checkout-1
      // So we can check in on someone's checkout day (same-day turnover)
      
      // Check for overlap, but allow same-day turnover
      const hasOverlap = checkIn < resCheckOut && checkOut > resCheckIn;
      const isSameDayTurnover = checkIn.getTime() === resCheckOut.getTime();
      
      // Return true only if there's overlap AND it's NOT same-day turnover
      return hasOverlap && !isSameDayTurnover;
    });
  };

  // Get booked dates for a room (formatted string)
  const getRoomBookedDates = (room: Room): string => {
    if (!room.reservations || room.reservations.length === 0) return '';
    
    const activeReservations = (room.reservations as any[])
      .filter((r: any) => r.status !== 'CANCELLED' && r.status !== 'CHECKED_OUT')
      .sort((a: any, b: any) => 
        new Date(a.checkInDate).getTime() - new Date(b.checkInDate).getTime()
      );
    
    if (activeReservations.length === 0) return '';
    
    // Format dates: "Jan 15 - Jan 20, Jan 25 - Jan 30"
    const dateRanges = activeReservations.map((res: any) => {
      const checkIn = new Date(res.checkInDate);
      const checkOut = new Date(res.checkOutDate);
      const formatDate = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${formatDate(checkIn)} - ${formatDate(checkOut)}`;
    });
    
    return dateRanges.join(', ');
  };

  // Show all rooms (including reserved) - backend will prevent double-booking
  const handleCheckAvailability = async () => {
    if (!isAuthenticated || !user) {
      toast({
        title: 'Authentication Required',
        description: 'Please login or register first to check room availability. You will be redirected to the login page.',
        variant: 'destructive',
      });
      localStorage.setItem('reservationFormData', JSON.stringify(formData));
      localStorage.setItem('reservationReturnUrl', window.location.pathname);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      return;
    }
    
    if (!formData.hotel || !formData.roomType || !formData.arrivingDate || !formData.departureDate) {
      toast({
        title: 'Validation Error',
        description: 'Please select hotel, room type, and dates first.',
        variant: 'destructive',
      });
      return;
    }

    const checkIn = new Date(formData.arrivingDate);
    const checkOut = new Date(formData.departureDate);
    
    if (checkOut <= checkIn) {
      toast({
        title: 'Invalid Dates',
        description: 'Check-out date must be after check-in date.',
        variant: 'destructive',
      });
      return;
    }

    setCheckingAvailability(true);
    setHasCheckedAvailability(true);
    try {
      // Filter rooms from already loaded rooms (from /api/public/accommodations)
      // Show ALL rooms including reserved ones - backend validation will prevent double-booking
      // But exclude inactive rooms (under maintenance)
      const filteredRooms = rooms.filter((room: Room) => {
        const matchesHotel = room.companyId === formData.hotel;
        const matchesRoomType = !formData.roomType || room.roomTypeId === formData.roomType;
        const matchesCapacity = !formData.numberOfGuests || (room.capacity || 0) >= parseInt(formData.numberOfGuests);
        const isActiveRoom = room.isActive; // Filter out inactive rooms
        
        return matchesHotel && matchesRoomType && matchesCapacity && isActiveRoom;
      });

      if (filteredRooms.length === 0) {
        const roomsOfType = rooms.filter((room) => 
          room.companyId === formData.hotel && 
          room.roomTypeId === formData.roomType
        );

        if (roomsOfType.length === 0) {
          toast({
            title: 'No Rooms of This Type',
            description: `No rooms of the selected type exist in this hotel. Please select a different room type.`,
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'No Rooms Found',
            description: `No rooms match your criteria. Please adjust your selection.`,
            variant: 'destructive',
          });
        }
        setAvailableRooms([]);
        setSelectedRoomId('');
      } else {
        // Show all rooms (including reserved) - backend will validate dates on submit
        setAvailableRooms(filteredRooms);
        if (filteredRooms.length === 1) {
          setSelectedRoomId(filteredRooms[0].id);
          setTimeout(() => autoApplyPromotion(), 100);
        }
        toast({
          title: 'Rooms Found',
          description: `Found ${filteredRooms.length} room(s). Reserved rooms are marked - backend will prevent double-booking.`,
        });
      }
    } catch (error: any) {
      console.error('Failed to load rooms:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load rooms. Please try again.',
        variant: 'destructive',
      });
      setAvailableRooms([]);
      setSelectedRoomId('');
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Find or create guest
  const findOrCreateGuest = async (companyId: string, name: string, email: string): Promise<string | null> => {
    try {
      // Validate email format
      const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
      };

      // Use logged-in user's information if available, otherwise use form data
      let firstName: string;
      let lastName: string;
      let guestEmail: string | null = null;

      if (user && isAuthenticated) {
        // Use user's information from auth context
        firstName = user.firstName || name.trim().split(/\s+/)[0] || 'Guest';
        lastName = user.lastName || name.trim().split(/\s+/).slice(1).join(' ') || 'User';
        // Validate user email before using it
        const userEmailCandidate = user.email || email.trim();
        guestEmail = userEmailCandidate && isValidEmail(userEmailCandidate) ? userEmailCandidate : null;
      } else {
        // Use form data
        const nameParts = name.trim().split(/\s+/);
        firstName = nameParts[0] || 'Guest';
        lastName = nameParts.slice(1).join(' ') || 'User';
        const emailCandidate = email.trim();
        guestEmail = emailCandidate && isValidEmail(emailCandidate) ? emailCandidate : null;
      }

      // Try to find existing guest by email (only if valid email)
      if (guestEmail) {
        const guests = await apiService.getHotelGuests({ companyId, search: guestEmail });
        const existingGuest = Array.isArray(guests) 
          ? guests.find((g: any) => g.email === guestEmail) 
          : null;

        if (existingGuest) {
          return existingGuest.id;
        }
      }

      // Create new guest
      const guestData: any = {
        companyId,
        firstName,
        lastName,
      };

      // Only add email if it's valid
      if (guestEmail) {
        guestData.email = guestEmail;
      }

      // Include phone number if available from user profile
      if (user?.phone && user.phone.trim()) {
        guestData.phone = user.phone.trim();
      }

      // Include other user details if available
      if (user?.country && user.country.trim()) {
        guestData.country = user.country.trim();
      }

      if (user?.passportNumber && user.passportNumber.trim()) {
        guestData.passportNumber = user.passportNumber.trim();
      }

      if (user?.idNumber && user.idNumber.trim()) {
        guestData.idNumber = user.idNumber.trim();
      }

      const newGuest = await apiService.createHotelGuest(guestData);
      return newGuest.id;
    } catch (error: any) {
      console.error('Failed to find/create guest:', error);
      throw new Error(error.message || 'Failed to create guest profile');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if user is authenticated
    if (!isAuthenticated || !user) {
      toast({
        title: 'Authentication Required',
        description: 'Please register or login first to make a reservation.',
        variant: 'destructive',
      });
      // Store form data and redirect to login
      localStorage.setItem('reservationFormData', JSON.stringify(formData));
      localStorage.setItem('reservationReturnUrl', '/contact');
      navigate('/login');
      return;
    }
    
    if (!formData.hotel || !formData.roomType || !formData.arrivingDate || !formData.departureDate) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    // Validate dates
    const checkIn = new Date(formData.arrivingDate);
    const checkOut = new Date(formData.departureDate);
    
    if (checkOut <= checkIn) {
      toast({
        title: 'Invalid Dates',
        description: 'Check-out date must be after check-in date.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Use selected room or check availability
      let selectedRoom;
      
      if (selectedRoomId && availableRooms.length > 0) {
        // Use user-selected room from availability check
        selectedRoom = availableRooms.find((r: any) => r.id === selectedRoomId);
      } else {
        // Fallback: Check availability now
        const availabilityResult = await apiService.checkRoomAvailability({
          companyId: formData.hotel,
          checkInDate: formData.arrivingDate,
          checkOutDate: formData.departureDate,
          roomTypeId: formData.roomType,
          numberOfGuests: parseInt(formData.numberOfGuests) || 1,
        });

        if (!availabilityResult.rooms || availabilityResult.rooms.length === 0) {
          toast({
            title: 'No Rooms Available',
            description: `All rooms of this type are reserved for the selected dates (${formData.arrivingDate} to ${formData.departureDate}). Please try different dates or select another room type.`,
            variant: 'destructive',
          });
          setIsSubmitting(false);
          return;
        }

        const availableRoomIds = availabilityResult.rooms.map((r: any) => r.id);
        selectedRoom = rooms.find(
          (room) => 
            availableRoomIds.includes(room.id) &&
            room.companyId === formData.hotel && 
            room.roomTypeId === formData.roomType && 
            room.isActive
        );
      }

      if (!selectedRoom) {
        toast({
          title: 'Room Selection Error',
          description: 'Please select a room from the available options or check availability again.',
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }

      // Create or find guest
      const guestId = await findOrCreateGuest(
        formData.hotel,
        formData.name,
        formData.email
      );

      if (!guestId) {
        throw new Error('Failed to create guest profile');
      }

      // Calculate total amount
      const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
      const totalAmount = (selectedRoom.basePricePerNight || 0) * nights;

      // Create reservation - use UTC midnight to avoid timezone issues
      // Parse date string (YYYY-MM-DD) and create UTC date at midnight
      const [checkInYear, checkInMonth, checkInDay] = formData.arrivingDate.split('-').map(Number);
      const [checkOutYear, checkOutMonth, checkOutDay] = formData.departureDate.split('-').map(Number);
      
      const checkInDate = new Date(Date.UTC(checkInYear, checkInMonth - 1, checkInDay, 0, 0, 0, 0)).toISOString();
      const checkOutDate = new Date(Date.UTC(checkOutYear, checkOutMonth - 1, checkOutDay, 0, 0, 0, 0)).toISOString();

      await apiService.createHotelReservation({
        companyId: formData.hotel,
        roomId: selectedRoom.id,
        guestId,
        checkInDate,
        checkOutDate,
        numberOfGuests: parseInt(formData.numberOfGuests) || 1,
        totalAmount,
        depositAmount: 0,
        promotionId: formData.promotionId || undefined,
      });

      toast({
        title: 'Success',
        description: 'Reservation request submitted successfully! We will contact you soon to confirm your booking.',
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        hotel: '',
        roomType: '',
        numberOfGuests: '',
        arrivingDate: '',
        departureDate: '',
        promotionId: '',
      });
      setAvailableRooms([]);
      setSelectedRoomId('');
      setAppliedPromotions([]);
      setHasCheckedAvailability(false);
    } catch (error: any) {
      // Handle specific error cases
      if (error.message?.includes('availability') || error.message?.includes('reserved')) {
        toast({
          title: 'No Rooms Available',
          description: `All rooms are reserved for the selected dates. Please try different dates or contact us at ${contactInfo.find(c => c.icon === Mail)?.value || 'info@ikorohouse.com'} for assistance.`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: error.message || 'Failed to submit reservation. Please try again or contact us directly.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <LandingNavbar />
      <main className="flex-1">
            <LandingImageCarousel />
      <div
          className="w-full min-h-[50vh] sm:h-[55vh] md:h-[60vh] relative"
          style={{ backgroundColor: "#000000" }}
        >
          <div className="h-full max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-12">
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 items-center h-full">
              {/* Left Side - Image */}
              <div className="order-2 md:order-1 h-full min-h-[250px] sm:min-h-[300px] md:h-full overflow-hidden">
                <img
                  src="/images/webflow/main.jpg"
                  alt="Ikoro House Interior"
                  className="w-full h-full object-cover rounded-lg sm:rounded-b-lg shadow-lg"
                />
              </div>

              {/* Right Side - Text Content */}
              <div className="order-1 md:order-2 space-y-3 sm:space-y-4">
                <h2
                  className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white"
                >
                  Welcome to our quietly comfortable rooms at Ikoro House.
                </h2>
                <div
                  className="space-y-2 sm:space-y-3 text-xs sm:text-sm md:text-base leading-relaxed text-white"
                >
                  <p>
                    ‍Discover the perfect blend of classic style and modern
                    comfort at Ikoro House. Our rooms offer spacious, cozy beds
                    for a peaceful night's rest. For business travelers, each
                    room includes a dedicated workspace and complimentary
                    high-speed WiFi. At Ikoro House, your comfort is paramount.
                    Enjoy 24-hour room service for your cravings. Choose from
                    our six unique room options: Iwacu, Karisimbi, Bisoke,
                    Muhabura, Sabyinyo, and Gahinga. Each room offers a
                    personalized experience. Our team is dedicated to seamlessly
                    blending tranquility and comfort. Whether you're in Kigali
                    for a safari stopover or a business trip, our thoughtfully
                    designed rooms cater to your needs. Experience the best of
                    Kigali in ultimate comfort at Ikoro House.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full py-8 sm:py-12 md:py-16 bg-white">
      {/* <div className="border-t-2 border-amber-800 mb-8"></div> */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={contactSectionRef} className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
          {/* Left Section - Contact Information */}
          <div className="space-y-4 sm:space-y-6">
            {contactInfo.map((contact, index) => {
              const IconComponent = contact.icon;
              return (
                <div 
                  key={index} 
                  className="flex items-start gap-3 sm:gap-4 transition-all duration-700 ease-out"
                  style={{ 
                    transitionDelay: `${index * 100}ms`,
                    opacity: isContactVisible ? 1 : 0,
                    transform: isContactVisible 
                      ? 'perspective(1000px) translateZ(0) scale(1)' 
                      : 'perspective(1000px) translateZ(-200px) scale(0.7)'
                  }}
                >
                  <IconComponent 
                    className="w-4 h-4 sm:w-5 sm:h-5 mt-1 flex-shrink-0 text-black" 
                    strokeWidth={1.5} 
                  />
                  <div className="flex-1">
                    <div
                      className="font-semibold text-xs sm:text-sm mb-1"
                      style={{ color: '#2C2416' }}
                    >
                      {contact.label}
                    </div>
                    <div
                      className="text-sm sm:text-base"
                      style={{ color: '#2C2416' }}
                    >
                      {contact.icon === Phone ? (
                        <a href={`tel:${contact.value.replace(/\s+/g, '')}`} className="hover:text-amber-600 transition-colors">
                          {contact.value}
                        </a>
                      ) : (
                        contact.value
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Section - Map */}
          <div 
            className="w-full h-full min-h-[300px] sm:min-h-[400px] relative transition-all duration-700 ease-out"
            style={{ 
              transitionDelay: '300ms',
              opacity: isContactVisible ? 1 : 0,
              transform: isContactVisible 
                ? 'perspective(1000px) translateZ(0) scale(1)' 
                : 'perspective(1000px) translateZ(-200px) scale(0.7)'
            }}
          >
            {!mapLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Loading map...</p>
                </div>
              </div>
            )}
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3987.5207!2d30.0707936!3d-1.9884906!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x19dca7e12a445d53%3A0x393fd65388aa8e7e!2sIKORO%20HOUSE%20-%20Rebero!5e0!3m2!1sen!2srw!4v1737974400000!5m2!1sen!2srw"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '300px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ikoro House Rebero Location"
              className="rounded-lg"
              onLoad={() => setMapLoaded(true)}
              onClick={() => window.open('https://maps.app.goo.gl/8qeBhwepRvpMcgwC9', '_blank')}
            ></iframe>
          </div>
        </div>
      </div>
    </div>


      </main>
      <LandingFooter />
    </div>
  );
}